import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Toaster } from '@notino/react-styleguide';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { usePageLoadTopScroll } from '@hooks';
import { IEcommerceServices } from '@model';
import { useLocalizationContext, useRouteLocalization } from '@services/localizationService';
import { usePageLoadMessaging } from '@services/messagingService';
import { useRedirect } from '@services/routingService';
import { StyledCheckoutWrapper } from '@styles/generalStyles';
import { EPage, ReactFCV } from '@types';
import { withProviders } from '@utils/config';

import {
  CartDiscountsWrapper,
  CartFreeDeliveryWrapper,
  CartPageButtonRowWrapper,
  CartPageContentSkeleton,
  CartPageSkeleton,
  CartPayPalExpressCheckout,
  CartProductsWrapper,
  FinalPriceRowWrapper,
  Gifts,
  LoyaltyDiscountItemWrapper,
  LoyaltyFreeDeliveryItemWrapper,
  ServiceWrapper,
  VoucherWrapper,
} from './components';
import { LoyaltyBannerWrapper } from './components/CartLoyaltyWrapper';
import { CartPageContextProvider, useCartPageContext, ECartPageState } from './context';
import { useCartPageModals, useCartPageEcommerce } from './hooks';
import { messages } from './messages';
import { StyledCartBody, StyledCartContent, StyledCartTitle } from './styled';

const CartPageComponent: ReactFCV = () => {
  const {
    state: { cartPageState, products, serviceItems, pricesSummary, gifts, benefits, loyaltyRewardRemoved },
    settings,
  } = useCartPageContext();
  const {
    applicationSettings: {
      features: { enableLoyalty },
    },
  } = useApplicationRuntimeContext();
  const { formatMessage } = useIntl();

  const { translationsReady } = useLocalizationContext();
  const ecommerceServices: IEcommerceServices = { settings: settings, gifts: gifts.some((x) => !x.hideInCart) };

  useRouteLocalization(EPage.CART);
  useCartPageModals();
  usePageLoadMessaging();
  usePageLoadTopScroll();
  useCartPageEcommerce(products, pricesSummary, ecommerceServices, serviceItems, benefits);

  const redirectMethod = useRedirect();

  useEffect(() => {
    if (cartPageState === ECartPageState.DEFAULT && products.length === 0 && serviceItems.discoveryBox.length === 0) {
      redirectMethod(EPage.HOME_PAGE);
    }
  }, [products, cartPageState, redirectMethod, serviceItems.discoveryBox]);

  if (!translationsReady) {
    return <CartPageSkeleton />;
  }

  if (loyaltyRewardRemoved && enableLoyalty) {
    Toaster.info({ content: formatMessage(messages.loyaltyToastMessage), icon: 'IconRegularCheckmark' });
  }

  return (
    <StyledCheckoutWrapper>
      <Toaster />
      <StyledCartTitle>
        <FormattedMessage {...messages.cartTitle} />
      </StyledCartTitle>
      <StyledCartBody>
        {cartPageState === ECartPageState.LOADING && <CartPageContentSkeleton />}
        {canRenderCartPageContent(products.length, serviceItems.discoveryBox.length, cartPageState) && (
          <StyledCartContent>
            <CartFreeDeliveryWrapper />
            <CartProductsWrapper />
            <Gifts />
            <ServiceWrapper />
            <LoyaltyFreeDeliveryItemWrapper />
            <LoyaltyDiscountItemWrapper />
            <CartDiscountsWrapper />
            <VoucherWrapper />
            <LoyaltyBannerWrapper />
            <FinalPriceRowWrapper />
            <CartPayPalExpressCheckout />
          </StyledCartContent>
        )}
      </StyledCartBody>
      <CartPageButtonRowWrapper />
    </StyledCheckoutWrapper>
  );
};

const canRenderCartPageContent = (productAmount: number, discoBoxAmount: number, cartPageState: ECartPageState) =>
  (productAmount > 0 || discoBoxAmount > 0) && cartPageState !== ECartPageState.LOADING;

const CartPage = withProviders(CartPageContextProvider)(CartPageComponent);
export default CartPage;
